import {
  ArrowLeft,
  Check,
  CheckCircle2,
  Copy,
  ExternalLink,
  Info,
} from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router'
import {
  AGENTS,
  DATABASE_STEPS,
  DEPLOY_STEPS,
} from '@/components/onboarding-sheet/agents'
import { useOnboardingSheet } from '@/components/onboarding-sheet/context'
import { InlineApiKey } from '@/components/onboarding-sheet/inline-api-key'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
} from '@/components/ui/item'
import { Progress } from '@/components/ui/progress'
import { useCompanyPath } from '@/lib/hooks'
import { cn } from '@/lib/utils'
import type { PathStep, SetupStep } from '@/components/onboarding-sheet/agents'
import type { Pathname } from '@/lib/hooks/use-company-path'

const CodeBlock = ({ code }: { code: string }) => {
  const [isCopied, setIsCopied] = useState(false)

  const handleCopy = async () => {
    await navigator.clipboard.writeText(code)
    setIsCopied(true)
    setTimeout(() => setIsCopied(false), 2000)
  }

  return (
    <div className="relative rounded-lg bg-muted border border-border overflow-hidden text-xs">
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-border bg-muted/50">
        <span className="text-muted-foreground font-mono">code</span>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1 px-2 py-0.5 rounded hover:bg-background text-muted-foreground hover:text-foreground transition-colors"
        >
          {isCopied ? (
            <>
              <Check className="size-3" />
              Copied
            </>
          ) : (
            <>
              <Copy className="size-3" />
              Copy
            </>
          )}
        </button>
      </div>
      <pre className="p-3 font-mono overflow-x-auto leading-relaxed text-foreground whitespace-pre-wrap break-all">
        <code>{code}</code>
      </pre>
    </div>
  )
}

type StepRowProps = {
  index: number
  title: string
  description: string
  isCompleted: boolean
  isExpanded: boolean
  onToggle: () => void
  action?: React.ReactNode
  children?: React.ReactNode
}

const StepRow = ({
  index,
  title,
  description,
  isCompleted,
  isExpanded,
  onToggle,
  action,
  children,
}: StepRowProps) => {
  const isLocked = !isCompleted && !isExpanded
  return (
    <Item variant="outline" className="rounded-xl">
      <ItemMedia>
        <Checkbox
          checked={isCompleted}
          onCheckedChange={() => onToggle()}
          disabled={isLocked}
          aria-label={
            isCompleted ? 'Mark step incomplete' : 'Mark step complete'
          }
          className="size-5"
        />
      </ItemMedia>
      <ItemContent>
        <ItemTitle
          className={cn(
            isCompleted && 'text-muted-foreground line-through',
            isLocked && 'text-muted-foreground'
          )}
        >
          {index + 1}. {title}
        </ItemTitle>
        {isExpanded && <ItemDescription>{description}</ItemDescription>}
        {isExpanded && children}
      </ItemContent>
      {action && isExpanded && (
        <ItemActions className="self-start">{action}</ItemActions>
      )}
    </Item>
  )
}

type AgentStepRowProps = {
  index: number
  step: SetupStep
  isCompleted: boolean
  isExpanded: boolean
  onToggle: () => void
  apiKeyToken: string | null
  onApiKeyCreated: (token: string) => void
}

const AgentStepRow = ({
  index,
  step,
  isCompleted,
  isExpanded,
  onToggle,
  apiKeyToken,
  onApiKeyCreated,
}: AgentStepRowProps) => {
  return (
    <StepRow
      index={index}
      title={step.title}
      description={step.description}
      isCompleted={isCompleted}
      isExpanded={isExpanded}
      onToggle={onToggle}
    >
      {step.isApiKeyStep && (
        <InlineApiKey
          onCreated={(token) => {
            onApiKeyCreated(token)
            onToggle()
          }}
          createdToken={apiKeyToken}
        />
      )}
      {step.code && <CodeBlock code={step.code} />}
      {step.note && (
        <Alert>
          <Info />
          <AlertDescription>{step.note}</AlertDescription>
        </Alert>
      )}
    </StepRow>
  )
}

type DeployStepRowProps = {
  index: number
  step: PathStep
  isCompleted: boolean
  isExpanded: boolean
  onToggle: () => void
}

const DeployStepRow = ({
  index,
  step,
  isCompleted,
  isExpanded,
  onToggle,
}: DeployStepRowProps) => {
  const { getCompanyPath } = useCompanyPath()
  const { close } = useOnboardingSheet()

  const isExternal = step.link?.startsWith('http')

  return (
    <StepRow
      index={index}
      title={step.title}
      description={step.description}
      isCompleted={isCompleted}
      isExpanded={isExpanded}
      onToggle={onToggle}
      action={
        step.link ? (
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-2 text-xs"
            asChild
          >
            {isExternal ? (
              <a href={step.link} target="_blank" rel="noopener noreferrer">
                Open docs
                <ExternalLink className="size-3" />
              </a>
            ) : (
              <Link
                to={getCompanyPath({ pathname: step.link as Pathname })}
                onClick={close}
              >
                Open
              </Link>
            )}
          </Button>
        ) : undefined
      }
    >
      {step.links && step.links.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {step.links.map((docsLink) => (
            <Button
              key={docsLink.url}
              variant="outline"
              size="sm"
              className="h-7 px-2 text-xs"
              asChild
            >
              <a href={docsLink.url} target="_blank" rel="noopener noreferrer">
                {docsLink.label}
                <ExternalLink className="size-3" />
              </a>
            </Button>
          ))}
        </div>
      )}
    </StepRow>
  )
}

export const StepsView = () => {
  const {
    selectedPath,
    selectedAgent,
    completedSteps,
    toggleStep,
    completeStep,
    totalSteps,
    setView,
    resetPath,
    dismiss,
  } = useOnboardingSheet()
  const [apiKeyToken, setApiKeyToken] = useState<string | null>(null)

  const handleBack = () => {
    if (selectedPath === 'agent') {
      setView('agent-select')
    } else {
      setView('cta')
    }
    resetPath()
  }

  const agent =
    selectedPath === 'agent' && selectedAgent
      ? AGENTS.find((a) => a.id === selectedAgent)
      : null

  const isDeployPath =
    selectedPath === 'deploy' || selectedPath === 'database'
  const deploySteps: PathStep[] =
    selectedPath === 'deploy'
      ? DEPLOY_STEPS
      : selectedPath === 'database'
        ? DATABASE_STEPS
        : []

  const agentSteps = agent ? agent.steps : []
  const stepCount = agent ? agentSteps.length : deploySteps.length

  const allDone = completedSteps.length >= totalSteps && totalSteps > 0
  const progressValue =
    stepCount > 0 ? (completedSteps.length / stepCount) * 100 : 0

  const firstIncompleteIndex = (() => {
    for (let i = 0; i < stepCount; i++) {
      if (!completedSteps.includes(i)) {
        return i
      }
    }
    return -1
  })()

  const titleMap: Record<string, string> = {
    agent: agent ? `Connect ${agent.name}` : 'Connect AI agent',
    deploy: 'Deploy a service',
    database: 'Deploy a database',
  }
  const title = titleMap[selectedPath ?? ''] ?? 'Get started'

  return (
    <div className="flex flex-col h-full overflow-y-auto p-6 space-y-5">
      <div className="flex flex-col gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleBack}
          className="-ml-2 self-start text-muted-foreground"
        >
          <ArrowLeft className="size-4" />
          Back
        </Button>
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">{title}</h2>
          <Badge variant="secondary">
            {completedSteps.length}/{stepCount}
          </Badge>
        </div>
        <Progress value={progressValue} className="h-1" />
      </div>

      <div className="flex-1 flex flex-col gap-3">
        {agent
          ? agentSteps.map((step, i) => (
              <AgentStepRow
                key={i}
                index={i}
                step={step}
                isCompleted={completedSteps.includes(i)}
                isExpanded={i === firstIncompleteIndex}
                onToggle={() => toggleStep(i)}
                apiKeyToken={apiKeyToken}
                onApiKeyCreated={(token) => {
                  setApiKeyToken(token)
                  completeStep(0)
                }}
              />
            ))
          : isDeployPath &&
            deploySteps.map((step, i) => (
              <DeployStepRow
                key={i}
                index={i}
                step={step}
                isCompleted={completedSteps.includes(i)}
                isExpanded={i === firstIncompleteIndex}
                onToggle={() => toggleStep(i)}
              />
            ))}
      </div>

      {allDone && (
        <Alert>
          <CheckCircle2 />
          <AlertTitle>You're all set!</AlertTitle>
          <AlertDescription>
            {agent
              ? `${agent.name} is connected and ready to manage your Sevalla services.`
              : 'Your service is up and running.'}
          </AlertDescription>
        </Alert>
      )}

      <Button
        variant="ghost"
        size="sm"
        onClick={dismiss}
        className="self-center"
      >
        {allDone ? 'Close' : 'Skip for now'}
      </Button>
    </div>
  )
}
